import {
  ForwardedRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  UserConversationEntity,
  useTestIntervewMutation,
  useSendMessagesMutation,
  MessageEntity,
  MessageOptionType,
} from 'src/store/chatApi';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from 'src/store/userSlice';
import { selectMessages, setMessage } from 'src/store/messagesSlice';
import { v4 as uuidv4 } from 'uuid';
import AdminLogger from './adminLogger';
import MessageRow from './messageRow';
import { Panel } from 'src/ui/Panel';
import { EmptyState } from 'src/ui/EmptyState';
import { Input } from 'src/ui/Input';
import { Button } from 'src/ui/Button';
import { Icon } from 'src/ui/Icon';
import { useToast } from 'src/ui/Toast';

export type ChatRef = {
  onTestInterview: (id: number) => void;
};

const Chat = forwardRef((props, ref: ForwardedRef<ChatRef>) => {
  const toast = useToast();
  const currentUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const [currentConv, setCurrentCov] = useState<UserConversationEntity | null>(
    null
  );
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [testInterview] = useTestIntervewMutation();
  const [sendMessages] = useSendMessagesMutation();
  const messages = useSelector(
    selectMessages(currentConv?.conversationId || '')
  );

  useImperativeHandle(ref, () => ({
    onTestInterview: async (id: number) => {
      const response = await testInterview(id).unwrap();
      setCurrentCov(response);
    },
  }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onSend = async (message: string) => {
    if (!message.trim()) return;
    if (!currentConv) {
      toast({
        type: 'error',
        title: 'Error',
        message: 'Please select a question to test',
      });
      return;
    }
    setInputText('');
    try {
      const response = await sendMessages({
        userConversationId: currentConv?.id || '',
        message: { id: uuidv4(), text: message },
      }).unwrap();
      if (response.message) {
        dispatch(setMessage(response.message));
      }
      scrollToBottom();
    } catch (error: any) {
      toast({
        type: 'error',
        title: 'Error',
        message: error.message || error.error,
      });
    }
  };

  const onSendWithOption = async (message: Partial<MessageEntity>) => {
    if (!currentConv) {
      toast({
        type: 'error',
        title: 'Error',
        message: 'Please select a question to test',
      });
      return;
    }
    try {
      const response = await sendMessages({
        userConversationId: currentConv?.id || '',
        message: { id: uuidv4(), ...message },
      }).unwrap();
      if (response.message) {
        dispatch(setMessage(response.message));
      }
      scrollToBottom();
    } catch (error: any) {
      toast({
        type: 'error',
        title: 'Error',
        message: error.message || error.error,
      });
    }
  };

  const lastMessage = messages?.length ? messages[messages.length - 1] : null;
  const showInput = useMemo(() => {
    if (!lastMessage) return true;
    const { optionType } = lastMessage.options || {};
    if (!optionType) return true;
    if (
      [
        MessageOptionType.Single,
        MessageOptionType.Multiple,
        MessageOptionType.Dropdown,
        MessageOptionType.Range,
        MessageOptionType.Confirm,
        MessageOptionType.Location,
      ].includes(optionType)
    ) {
      return false;
    }
    return true;
  }, [lastMessage]);

  return (
    <Panel
      className="grow"
      title={currentConv?.conversation.topic || 'Test Interview'}
      icon="play-circle"
      bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
        {/* Chat message area */}
        <div
          className="grow scroll-y"
          style={{
            padding: 14,
            background: 'var(--bg-app)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!currentConv ? (
            <EmptyState
              icon="play-circle"
              title="Test the interview"
              desc="Click Test in the editor to start a live chat session."
            />
          ) : (
            <>
              {messages?.map((message) => (
                <MessageRow
                  key={message.id}
                  message={message}
                  isCurrentUser={message.senderId === currentUser?.id}
                  onSendWithOption={onSendWithOption}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Text input */}
        {currentConv && showInput && (
          <div
            className="row gap-2"
            style={{
              padding: '8px 12px',
              borderTop: '1px solid var(--border)',
              flexShrink: 0,
              background: 'var(--bg-canvas)',
            }}
          >
            <Input
              size="sm"
              className="grow"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={
                lastMessage?.options?.placeholder || 'Type message here'
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputText.trim()) {
                  onSend(inputText);
                }
              }}
            />
            <Button
              variant="primary"
              size="sm"
              iconOnly
              disabled={!inputText.trim()}
              onClick={() => onSend(inputText)}
            >
              <Icon name="send" size={15} />
            </Button>
          </div>
        )}

        {/* Admin log */}
        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid var(--border)',
          }}
        >
          <AdminLogger />
        </div>
      </div>
    </Panel>
  );
});

export default Chat;
